import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../AdminLayout';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import {
  CollectionProgressPanel,
  EnrollmentProgressPanel,
  EnrollmentFunnelPanel,
  FamilyPersonaPanel,
} from './AdminInsightsTab';

type InsightTab = 'collection' | 'enrollment' | 'funnel' | 'personas';

const TABS: { key: InsightTab; label: string }[] = [
  { key: 'collection', label: 'Monthly Collection' },
  { key: 'enrollment', label: 'Enrollment Progress' },
  { key: 'funnel',     label: 'Enrollment Funnel' },
  { key: 'personas',   label: 'Family Personas' },
];

const INSIGHT_TABS: readonly InsightTab[] = TABS.map(t => t.key);

export default function AdminInsights() {
  const [activeTab, setActiveTab]         = useUrlTabState<InsightTab>('tab', INSIGHT_TABS, 'collection');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Callback ref: storing nav in state re-triggers the effect when it finally mounts
  // (AdminLayout shows a loading spinner during auth, so the nav may not exist on
  // the first effect run — useRef wouldn't re-fire the effect when it appears).
  const [navEl, setNavEl] = useState<HTMLDivElement | null>(null);
  const [activeTabEl, setActiveTabEl] = useState<HTMLButtonElement | null>(null);

  const updateArrows = useCallback((el: HTMLDivElement) => {
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    if (!navEl) return;

    const onScroll = () => updateArrows(navEl);
    const onResize = () => updateArrows(navEl);

    updateArrows(navEl);

    navEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    const ro = new ResizeObserver(() => updateArrows(navEl));
    ro.observe(navEl);
    Array.from(navEl.children).forEach(child => ro.observe(child));

    return () => {
      navEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [navEl, updateArrows]);

  useEffect(() => {
    activeTabEl?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeTabEl]);

  const nudge = (dir: 'left' | 'right') => {
    navEl?.scrollBy({ left: dir === 'left' ? -180 : 180, behavior: 'smooth' });
  };

  return (
    <AdminLayout>
      <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">

        {/* Tab bar */}
        <div className="relative border-b border-gray-200 bg-white">

          {/* Left arrow + fade */}
          {canScrollLeft && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-14 bg-gradient-to-r from-white to-transparent z-10" />
              <button
                onClick={() => nudge('left')}
                aria-label="Scroll tabs left"
                className="absolute inset-y-0 left-0 z-20 flex items-center px-1 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </>
          )}

          <nav ref={el => setNavEl(el as HTMLDivElement | null)} className="flex overflow-x-auto no-scrollbar px-4 sm:px-6">
            {TABS.map(tab => (
              <button
                key={tab.key}
                ref={activeTab === tab.key ? setActiveTabEl : undefined}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Right arrow + fade */}
          {canScrollRight && (
            <>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-white to-transparent z-10" />
              <button
                onClick={() => nudge('right')}
                aria-label="Scroll tabs right"
                className="absolute inset-y-0 right-0 z-20 flex items-center px-1 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'collection' && <CollectionProgressPanel />}
          {activeTab === 'enrollment' && <EnrollmentProgressPanel />}
          {activeTab === 'funnel'     && <EnrollmentFunnelPanel />}
          {activeTab === 'personas'   && <FamilyPersonaPanel />}
        </div>

      </div>
    </AdminLayout>
  );
}
